import { Anchor, Badge, Group, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { TEAMDAILY_STALE_DAYS, type TeamDailyTicket } from "@msc/shared";

import { prioAnzeige, stunden, tageSeit } from "./prioritaet.js";

/**
 * Eine Ticketzeile — **zwei Zeilen**, weil die Ticketliste je Mitarbeiter das
 * Wichtigste am Board ist und einzeilig zu wenig davon zeigt:
 *
 *   ▌ MS-1234 · Hoch · Kunde · 9 Tage still        7,5 h
 *     Titel des Vorgangs, bis zu zwei Zeilen
 *
 * Der farbige Balken links trägt die **Priorität** — auf einen Blick erkennbar,
 * ohne Platz für ein weiteres Etikett zu kosten. Die Vorgangsnummer verlinkt
 * nach Jira; **wartende** Vorgänge sind gedämpft, weil sie nicht auf dem Tisch
 * dessen liegen, in dessen Spalte sie stehen.
 */
export function TicketRow({
  ticket,
  jiraBaseUrl,
  zeigeBearbeiter = false,
  zeigeAlter = false,
}: {
  ticket: TeamDailyTicket;
  /** Basis-URL der Jira-Instanz; ohne sie bleibt die Nummer Text. */
  jiraBaseUrl?: string | null;
  /** Im Eingang steht statt des Bearbeiters „nicht zugewiesen". */
  zeigeBearbeiter?: boolean;
  /** Im Eingang zählt das Alter seit Anlage, im Board die Liegezeit. */
  zeigeAlter?: boolean;
}) {
  const { t } = useTranslation();
  const prio = prioAnzeige(ticket.priority);

  const stillTage = tageSeit(ticket.updated);
  const liegtStill = !ticket.isWaiting && stillTage >= TEAMDAILY_STALE_DAYS;
  const alterTage = tageSeit(ticket.created);

  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        padding: "4px 0",
        borderTop: "1px solid var(--mantine-color-default-border)",
        opacity: ticket.isWaiting ? 0.62 : 1,
      }}
    >
      <div
        title={`${t("pages.teamDaily.priority")}: ${t(prio.labelKey)}`}
        style={{
          flexShrink: 0,
          width: 3,
          borderRadius: 2,
          alignSelf: "stretch",
          background: `var(--mantine-color-${prio.farbe.replace(".", "-")})`,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Group gap={5} wrap="nowrap" justify="space-between">
          <Group gap={5} wrap="nowrap" style={{ minWidth: 0 }}>
            {jiraBaseUrl ? (
              <Anchor
                href={`${jiraBaseUrl.replace(/\/+$/, "")}/browse/${ticket.key}`}
                target="_blank"
                rel="noopener noreferrer"
                size="xs"
                ff="monospace"
                style={{ whiteSpace: "nowrap" }}
              >
                {ticket.key}
              </Anchor>
            ) : (
              <Text size="xs" ff="monospace" c="blue" style={{ whiteSpace: "nowrap" }}>
                {ticket.key}
              </Text>
            )}
            {/* Nur was ueber der Vorgabe des Schemas steht (s. `prioAnzeige`):
                Eine Prioritaet auf Vorgabewert ist keine Aussage. */}
            {prio.hervorheben && (
              <Text size="xs" c={prio.farbe} fw={600} style={{ whiteSpace: "nowrap" }}>
                {t(prio.labelKey)}
              </Text>
            )}
            {ticket.isWaiting && (
              <Badge size="xs" color="gray" variant="light">
                {t("pages.teamDaily.waiting")}
              </Badge>
            )}
            {liegtStill && (
              <Text
                size="xs"
                c="red.7"
                fw={600}
                style={{ whiteSpace: "nowrap" }}
                title={t("pages.teamDaily.staleHint", {
                  days: TEAMDAILY_STALE_DAYS,
                })}
              >
                {t("pages.teamDaily.stale", { days: stillTage })}
              </Text>
            )}
            {zeigeAlter && (
              <Text size="xs" c={alterTage >= TEAMDAILY_STALE_DAYS ? "red.7" : "dimmed"}>
                {t("pages.teamDaily.age", { days: alterTage })}
              </Text>
            )}
            {zeigeBearbeiter && (
              <Text size="xs" c="dimmed" truncate>
                {ticket.assigneeName ?? t("pages.teamDaily.unassigned")}
              </Text>
            )}
          </Group>
          {ticket.remainingSeconds > 0 && (
            <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              {stunden(ticket.remainingSeconds)}
            </Text>
          )}
        </Group>
        <Text size="xs" lineClamp={2} style={{ lineHeight: 1.3 }}>
          {ticket.summary}
        </Text>
        {ticket.customerLabel && (
          <Text size="xs" c="dimmed" truncate>
            {ticket.customerLabel}
          </Text>
        )}
      </div>
    </div>
  );
}
